import * as React from 'react';
import { Form, Text, Select, Checkbox } from 'react-form';
import { BaseFormProps } from '../../interfaces';
import { StyleConstraint, Language, StyleText, StyleCharacteristicForce, ScoreType, SurveyVersion, Style, StyleTag, Characteristic } from '../../models';
import { ScoreTypeDataService, LanguageDataService, CharacteristicDataService, StyleDataService, SurveyVersionDataService, NotificationService } from '../../services';
import { PonderaEditModal } from '../../components/inclinationmodals';
import { UserStore } from '../../stores';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';
// import { Checkbox } from 'react-bootstrap';

interface StyleEditState {
    scoreTypes: ScoreType[];
    styles: Style[];
    characteristics: Characteristic[];
    languages: Language[];
    surveyIsPublished: boolean;
}

interface StyleFormProps extends BaseFormProps {
    surveyVersionId: number;
    notificationService: NotificationService;
    UserStore?: UserStore;
}

@inject('UserStore') @observer
export class StyleForm extends React.Component<StyleFormProps, StyleEditState> {
    private scoreTypeDataService = new ScoreTypeDataService(this.props.notificationService);
    private styleDataService = new StyleDataService(this.props.notificationService);
    private surveyVersionDataService = new SurveyVersionDataService(this.props.notificationService);
    private characteristicDataService = new CharacteristicDataService(this.props.notificationService);
    private languageDataService = new LanguageDataService(this.props.notificationService);

    @computed get isSuperAdmin() {
        return this.props.UserStore!.userRoleIn(['superAdmin']);
    }

    constructor(props: StyleFormProps) {
        super(props);

        this.props.defaultValues.styleConstraints = this.props.defaultValues.styleConstraints || [];
        this.props.defaultValues.styleCharacteristicForces = this.props.defaultValues.styleCharacteristicForces || [];
        this.props.defaultValues.styleTexts = this.props.defaultValues.styleTexts || [];
        this.props.defaultValues.styleTags = this.props.defaultValues.styleTags || [];

        this.setState({
            scoreTypes: [],
            styles: [],
            characteristics: [],
            languages: [],
            surveyIsPublished: false
        });
    }

    public componentDidMount() {

        this.languageDataService.getAll().then((languages: Language[]) => {
            this.setState({
                languages: languages
            });
        });

        this.surveyVersionDataService.get(this.props.surveyVersionId).then((surveyVersion: SurveyVersion) => {
            this.setState({
                surveyIsPublished: surveyVersion.published !== null ? true : false
            });

            this.scoreTypeDataService.getAllBySurveyVersion(surveyVersion).then((scoreTypes: ScoreType[]) => {
                this.setState({
                    scoreTypes: scoreTypes
                });
            });

            this.characteristicDataService.getAllBySurveyVersion(surveyVersion).then((characteristics: Characteristic[]) => {
                this.setState({
                    characteristics: characteristics
                });
            });

            this.styleDataService.getAllBySurveyVersion(surveyVersion).then((styles: Style[]) => {
                this.setState({
                    styles: styles
                });
            });
        });
    }

    public render() {
        if (!this.state) {
            return <div />;
        }

        const scoreTypes: any = [];
        if (this.state.scoreTypes) {
            this.state.scoreTypes.map((x: ScoreType) => {
                scoreTypes.push({
                    label: x.name,
                    value: x.id
                });
            });
        }

        const characteristics: any = [{
            label: '[None]',
            value: undefined
        }];
        if (this.state.characteristics) {
            this.state.characteristics.map((x: Characteristic) => {
                characteristics.push({
                    label: x.name,
                    value: x.id
                });
            });
        }

        const styles: any = [];
        if (this.state.styles) {
            this.state.styles.map((x: Style) => {
                styles.push({
                    label: x.name + ' (' + x.core + ')',
                    value: x.id
                });
            });
        }

        const languageOpts: any = [];
        if (this.state.languages) {
            this.state.languages.map((x: Language) => {
                languageOpts.push({
                    label: x.name,
                    value: x.id
                });
            });
        }

        return (
            <Form defaultValues={this.props.defaultValues} onSubmit={submittedValues => this.props.submit(submittedValues)}>
                {formApi => (
                    <form onSubmit={formApi.submitForm} id="entityForm">
                        {this.state.surveyIsPublished && <div className="pva-alert">
                            <i>This survey version has already been published, so only content changes are allowed.</i>
                        </div>}

                        <fieldset>
                            <div className="form-group">
                                <label htmlFor="name">Style Name</label>
                                <Text className="form-control" field="name" disabled={!this.isSuperAdmin} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="name">Use Secondary Style</label>
                                <div>This will toggle between showing Primary and Secondary Styles when showing on Teamology and Individual Report</div>
                                <Checkbox className="form-control" field="useSecondaryStyle" disabled={!this.isSuperAdmin} />
                            </div>
                            
                            {!formApi.values.useSecondaryStyle && <div>

                                <div className="form-group">
                                    <label htmlFor="name">Style Sub Core</label>
                                    <div>Use a characteristic to display the sub type of the style. Only used if not using Secondary Style above.</div>
                                    <Select className="form-control" field="subCoreID" options={characteristics} />
                                </div>
                            </div>
                            }
                            
                            {(formApi.values.useSecondaryStyle || formApi.values.subCoreID > 0) && <div style={{paddingLeft: '50px'}}>
                                <div className="form-group">
                                    <label htmlFor="name">Style Sub Core Modal</label>
                                    {this.textBlock(formApi, languageOpts, 'subCore')}
                                </div>
                            </div>}

                            {!formApi.values.useSecondaryStyle && <div>
                                {(formApi.values.subCoreID === 0 || formApi.values.subCoreID === null || formApi.values.subCoreID === undefined) &&  <div className="form-group">
                                        <label htmlFor="name">Style Core</label>
                                        <div>Use a generic text for the sub type. Only shows if not using the secondary style, and no sub core characteristic is selected.</div>
                                        <Text className="form-control" field="core" disabled={!this.isSuperAdmin} />
                                    </div>
                                }
                            </div>}

                            <div className="form-group">
                                <label htmlFor="name">Sort</label><br />
                                <i>Determines the order in which the styles appear on the PVAStyles page.</i>
                                <Text className="form-control" field="sort" disabled={!this.isSuperAdmin} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="name">Sort</label><br />
                                <i>This determines in which order the constraints are matched for the styles. (This should only matter when there are multiple styles that could match, for example when the scores are the same for 2 characteristic scores)</i>
                                <Text className="form-control" field="sortCalc" disabled={!this.isSuperAdmin} />
                            </div>

                            <h3>Tags</h3>
                            <i>These are small badges under the PVA Style. These are purely visual.</i>
                            {
                                formApi.values.styleTags.map((styleTag: StyleTag, ind: number) => {
                                    return <div key={`st-${ind}`} className="pva-tag">
                                        <div className="row">
                                            <div className="col-xs-10">
                                                <Text className="form-control" field={['styleTags', ind, 'value']} />
                                            </div>
                                            <div className="col-xs-2">
                                                <a className="btn btn-danger form-control" onClick={(e) => { formApi.values.styleTags.splice(ind, 1); this.forceUpdate(); }}>Remove</a>
                                            </div>
                                        </div>
                                    </div>;
                                })
                            }
                            <div className="form-group">
                                <div className="row">
                                    <div className="col-xs-4">
                                        <a className="btn btn-default form-control" onClick={(e) => this.addStyleTag(formApi.values)}>Add Tag</a>
                                    </div>
                                </div>
                            </div>
                            
                        </fieldset>

                        <fieldset disabled={this.state.surveyIsPublished}>

                            {this.isSuperAdmin &&
                                <div>
                                    <h3>Constraints</h3>
                                    <i>These use an individuals Raw Score to determine if this Style is a match. If all constraints match, then it's assigned to the user.</i>
                                    {
                                        formApi.values.styleConstraints.map((styleConstraint: StyleConstraint, ind: number) => {
                                            return <div key={ind}>
                                                <div className="row">
                                                    <div className="col-xs-5">
                                                        <div className="form-group">
                                                            <label htmlFor="name">Score Type</label>
                                                            <Select className="form-control" field={['styleConstraints', ind, 'scoreTypeLeft', 'id']} options={scoreTypes} />
                                                        </div>
                                                    </div>
                                                    <div className="col-xs-2">
                                                        <div className="text-center">
                                                            <label>Greater</label><br />
                                                            <label>Than</label>
                                                        </div>
                                                    </div>
                                                    <div className="col-xs-5">
                                                        <div className="form-group">
                                                            <label htmlFor="name">Score Type</label>
                                                            <Select className="form-control" field={['styleConstraints', ind, 'scoreTypeRight', 'id']} options={scoreTypes} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>;
                                        })}

                                    <div className="form-group">
                                        <div className="row">
                                            <div className="col-xs-4">
                                                <a className="btn btn-default form-control" onClick={this.addStyleConstraintBinder()}>Add Constraint</a>
                                            </div>
                                        </div>
                                    </div>

                                    <hr />

                                    <h3>Secondary Style</h3>
                                    <div className="form-group">
                                        <Select className="form-control" field={['secondaryStyle', 'id']} options={styles} />
                                    </div>
                                    <h3>Tertiary Style</h3>
                                    <div className="form-group">
                                        <Select className="form-control" field={['tertiaryStyle', 'id']} options={styles} />
                                    </div>
                                    <h3>Least Preferred Style</h3>
                                    <div className="form-group">
                                        <Select className="form-control" field={['leastPreferredStyle', 'id']} options={styles} />
                                    </div>

                                    <hr />
                                </div>
                            }
                        </fieldset>

                        <h3>Text Content</h3>
                        <h5>About Style Brief</h5>

                        <table className="table">
                            <thead>
                                <tr>
                                    <td>
                                        Language
                                </td>
                                    <td>
                                        Text
                                </td>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    formApi.values.styleTexts.map((text: StyleText, index: number) => {
                                        if (text.styleTextType !== 'brief') {
                                            return;
                                        }

                                        const props = {
                                            show: text.showModal === true,
                                            onHide: (data: string) => {
                                                text.showModal = false;
                                                text.value = data;
                                                this.forceUpdate();
                                            },
                                            title: 'Content',
                                            content: text.value || ''
                                        };

                                        return <tr key={`styletext-${index}`}>
                                            <td>
                                                <Select className="form-control" field={['styleTexts', index, 'languageId']} options={languageOpts} />
                                            </td>
                                            <td>
                                                <a className="btn btn-default" onClick={(e) => { text.showModal = true; this.forceUpdate(); }}>View/Edit</a>
                                                <PonderaEditModal {...props} />
                                            </td>
                                        </tr>;
                                    })
                                }
                            </tbody>
                        </table>

                        <div className="form-group">
                            <div className="row">
                                <div className="col-xs-4">
                                    <a className="btn btn-default form-control" onClick={(e) => this.addBrief(formApi.values)}>Add Brief</a>
                                </div>
                            </div>
                        </div>

                        {/* <h5>About Style Content</h5>

                        <table className="table">
                            <thead>
                                <tr>
                                    <td>
                                        Language
                                </td>
                                    <td>
                                        Text
                                </td>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    formApi.values.styleTexts.map((text: StyleText, index: number) => {
                                        if (text.styleTextType !== 'content') {
                                            return;
                                        }

                                        const props = {
                                            show: text.showModal === true,
                                            onHide: (data: string) => {
                                                text.showModal = false;
                                                text.value = data;
                                                this.forceUpdate();
                                            },
                                            title: 'Content',
                                            content: text.value || ''
                                        };

                                        return <tr key={`styletext-${index}`}>
                                            <td>
                                                <Select className="form-control" field={['styleTexts', index, 'languageId']} options={languageOpts} />
                                            </td>
                                            <td>
                                                <a className="btn btn-default" onClick={(e) => { text.showModal = true; this.forceUpdate(); }}>View/Edit</a>
                                                <PonderaEditModal {...props} />
                                            </td>
                                        </tr>;
                                    })
                                }
                            </tbody>
                        </table>
                        
                        <div className="form-group">
                            <div className="row">
                                <div className="col-xs-4">
                                    <a className="btn btn-default form-control" onClick={(e) => this.addContent(formApi.values)}>Add Content</a>
                                </div>
                            </div>
                        </div> */}

                        <h5>Top Modal Content</h5>
                        {this.textBlock(formApi, languageOpts, 'top')}
                        <h5>Left Modal Content</h5>
                        {this.textBlock(formApi, languageOpts, 'left')}
                        <h5>Right Modal Content</h5>
                        {this.textBlock(formApi, languageOpts, 'right')}
                        <h5>Bottom Modal Content</h5>
                        {this.textBlock(formApi, languageOpts, 'bottom')}
                        <h5>Team Content</h5>
                        {this.textBlock(formApi, languageOpts, 'team')}

                        <hr />

                        <h3>Badges</h3>
                        {this.textBlocks(formApi, languageOpts, 'additional', true, true)}

                        <fieldset>
                            {this.isSuperAdmin && <div>
                                <h3>Interaction Report Force</h3>
                                <i>If a user is assigned this style, the following characteristics will be forced to show on the Interaction Report regardless of the individuals Characteristic score.</i>
                                {
                                    formApi.values.styleCharacteristicForces.map((styleCharacteristicForce: StyleCharacteristicForce, ind: number) => {
                                        return <div className="form-group" key={ind}>
                                            <label htmlFor="name">Characteristic</label>
                                            <div className="row">
                                                <div className="col-xs-10">
                                                    <Select className="form-control" field={['styleCharacteristicForces', ind, 'characteristicID']} options={characteristics} />
                                                </div>
                                                <div className="col-xs-2">
                                                    <a className="btn btn-danger btn-block" onClick={(e: any) => this.removeSCF(formApi, ind)}>Remove</a>
                                                </div>
                                            </div>
                                        </div>;
                                    })}

                                <div className="form-group">
                                    <div className="row">
                                        <div className="col-xs-4">
                                            <a className="btn btn-default form-control" onClick={this.addStyleCharacteristicForceBinder()}>Add Force Characteristic</a>
                                        </div>
                                    </div>
                                </div>

                                <hr />
                            </div>}
                        </fieldset>

                        <button type="submit" className="btn btn-primary">Save</button>
                    </form>
                )}
            </Form>
        );
    }

    private removeSCF(formApi: any, ind: number) {
        var styleCharacteristicForces = formApi.values.styleCharacteristicForces;
        styleCharacteristicForces.splice(ind, 1);
        formApi.setValue('styleCharacteristicForces', styleCharacteristicForces);
        this.forceUpdate();
    }

    private textBlock(formApi: any, languageOpts: any[], type: string) {
        return (
            <div>
                <table className="table">
                    <tr>
                        <td>
                            Language
                        </td>
                        <td>
                            Text
                        </td>
                    </tr>
                    {
                        formApi.values.styleTexts.map((text: StyleText, index: number) => {
                            if (text.styleTextType !== type) {
                                return;
                            }

                            const props = {
                                show: text.showModal === true,
                                onHide: (data: string) => {
                                    text.showModal = false;
                                    text.value = data;
                                    this.forceUpdate();
                                },
                                title: 'Content',
                                content: text.value || ''
                            };

                            return <tr key={`styletext-${index}`}>
                                <td>
                                    <Select className="form-control" field={['styleTexts', index, 'languageId']} options={languageOpts} />
                                </td>
                                <td>
                                    <a className="btn btn-default" onClick={(e) => { text.showModal = true; this.forceUpdate(); }}>View/Edit</a>
                                    <PonderaEditModal {...props} />
                                </td>
                            </tr>;
                        })
                    }
                </table>
                <div className="form-group">
                    <div className="row">
                        <div className="col-xs-4">
                            <a className="btn btn-default form-control" onClick={(e) => this.add(formApi.values, type)}>Add</a>
                        </div>
                    </div>
                </div>
            </div>);
    }

    private textBlocks(formApi: any, languageOpts: any[], type: string, showSort?: boolean, showRemove?: boolean) {
        return (
            <div>
                <table className="table">
                    <thead>
                        <tr>
                            <th>
                                Language
                            </th>
                            <th>
                                Name
                            </th>
                            {showSort && <th>Sort</th>}
                            <th>
                                Text
                            </th>
                            {showRemove && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                    {
                        formApi.values.styleTexts.map((text: StyleText, index: number) => {
                            if (text.styleTextType !== type) {
                                return;
                            }

                            const props = {
                                show: text.showModal === true,
                                onHide: (data: string) => {
                                    text.showModal = false;
                                    text.value = data;
                                    this.forceUpdate();
                                },
                                title: 'Content',
                                content: text.value || ''
                            };

                            return <tr key={`styletext-${index}`}>
                                <td>
                                    <Select className="form-control" field={['styleTexts', index, 'languageId']} options={languageOpts} />
                                </td>
                                <td>
                                    <Text className="form-control" field={['styleTexts', index, 'keyName']} />
                                </td>
                                {showSort && <td>
                                    <Text className="form-control" field={['styleTexts', index, 'sort']} />
                                </td>}
                                <td>
                                    <a className="btn btn-default" onClick={(e) => { text.showModal = true; this.forceUpdate(); }}>View/Edit</a>
                                    <PonderaEditModal {...props} />
                                </td>
                                {showRemove &&
                                    <td>
                                        <a className="btn btn-danger" onClick={(e) => { this.removeStyleText(formApi.values, index); }}>Remove</a>
                                    </td>
                                }
                            </tr>;
                        })
                    }
                    </tbody>
                </table>
                <div className="form-group">
                    <div className="row">
                        <div className="col-xs-4">
                            <a className="btn btn-default form-control" onClick={(e) => this.add(formApi.values, type)}>Add</a>
                        </div>
                    </div>
                </div>
            </div>);
    }

    private add(formValues: any, type: string) {
        var st = new StyleText();
        st.styleTextType = type;
        st.languageId = 1;
        st.sort = 0;
        st.keyName = '';
        formValues.styleTexts.push(st);
        this.forceUpdate();
    }

    removeStyleText = (formValues: any, index: number) => {
        formValues.styleTexts.splice(index, 1);
        this.forceUpdate();
    }
    
    private addStyleTag(formValues: any) {
        var st = new StyleTag();
        st.languageId = 1;
        formValues.styleTags.push(st);
        this.forceUpdate();
    }
    
    private addBrief(formValues: any) {
        this.add(formValues, 'brief');
    }
    
    private addStyleConstraint() {
        var styleConstraint = new StyleConstraint();
        this.props.defaultValues.styleConstraints.push(styleConstraint);
        this.forceUpdate();
    }

    private addStyleConstraintBinder(): ((event: React.MouseEvent<HTMLElement>) => void) {
        return this.addStyleConstraint.bind(this);
    }

    private addStyleCharacteristicForce() {
        var styleCharacteristicForce = new StyleCharacteristicForce();
        this.props.defaultValues.styleCharacteristicForces.push(styleCharacteristicForce);
        this.forceUpdate();
    }

    private addStyleCharacteristicForceBinder(): ((event: React.MouseEvent<HTMLElement>) => void) {
        return this.addStyleCharacteristicForce.bind(this);
    }
}


// WEBPACK FOOTER //
// ./src/components/style/StyleForm.tsx